import React, { useState, useEffect, useCallback } from "react";
import useAuth from "../hooks/UseAuth";
import { ref, set, get, child, database } from "../../firebase-config";
import { useParams, useNavigate } from "react-router-dom";
import { Container } from "react-bootstrap";
import { fetchQuestions } from "../utils/fetchUtils";
import formatToWIB from "../../shared/dateUtils";
import {
  saveUserToLocalStorage,
  getUserFromLocalStorage,
} from "../../shared/localStorage";
import {
  calculateResults,
  determinePersonality,
} from "../utils/personalityUtils";
import useTimer from "../hooks/UseTimer";
import Timer from "../components/Timer";
import PsikotesContent from "../components/PsikotesContent";
import PsikotesHeader from "../components/PsikotesHeader";
import Swal from "sweetalert2";
import "../styles/psikotes.css";

function Psikotes({ handleSubmitRef }) {
  const { user } = useAuth();
  const { psikotesId } = useParams();
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [completedPages, setCompletedPages] = useState([1]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const cardsPerPage = 6;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const questionsData = await fetchQuestions(psikotesId);
      setQuestions(questionsData);
      setLoading(false);
    };

    fetchData();
  }, [psikotesId]);

  const { time, formatTime } = useTimer();

  const handleOptionChange = (questionIndex, statementId, value, category) => {
    setSelectedOptions((prev) => {
      const newOptions = { ...prev };

      Object.keys(newOptions).forEach((key) => {
        if (
          newOptions[key].questionIndex === questionIndex &&
          newOptions[key].value === value
        ) {
          delete newOptions[key];
        }
      });

      newOptions[statementId] = { value, category, questionIndex };

      //? Console Untuk debugging selected category
      // const { counts } = calculateResults(newOptions);

      // console.log(`Selected ${value} for category ${category}`);
      // console.log("Category Counts:", counts);

      return newOptions;
    });
  };

  const handleSubmit = useCallback(async () => {
    const { counts, results } = calculateResults(selectedOptions);

    const completionTime = 15 * 60 - time; 
    const formattedCompletionTime = formatTime(completionTime);
    const currentDateWIB = formatToWIB(new Date());

    const personality = determinePersonality(results);

    //? Console Untuk debugging result dan personality
    // console.log("Results:", results);
    // console.log("Personality:", personality);

    const resultData = {
      categoryCounts: counts || { yes: {}, no: {} },
      finalResults: results || {},
      completionTime: formattedCompletionTime,
      completionDate: currentDateWIB,
      personality,
    };

    try {
      const resultsRef = ref(database, `results/${psikotesId}/${user.uid}/`);
      const snapshot = await get(resultsRef);
      const existingResults = snapshot.val() || {};

      // cek apakah result id sudah ada untuk user 
      let resultId = null;
      for (const [key, value] of Object.entries(existingResults)) {
        if (value) {
          resultId = key;
          break;
        }
      }

      if (!resultId) {
        resultId = `result_id_${Object.keys(existingResults).length + 1}`;
      }

      const resultRef = child(resultsRef, resultId);
      await set(resultRef, resultData);

      const userRef = ref(database, `users/${user.uid}`);
      const userSnapshot = await get(userRef);
      const userData = userSnapshot.val();

      const updatedUserData = {
        ...userData,
        status_tes: "Completed Test",
      };

      await set(userRef, updatedUserData);

      const { storedUser } = getUserFromLocalStorage();
      if (storedUser) {
        storedUser.status_tes = "Completed Test";
        saveUserToLocalStorage(storedUser);
      }

      navigate("/home", { replace: true });
      await Swal.fire({
        icon: "success",
        title: "Tes Selesai!",
        text: "Hasil tes Anda telah berhasil terkirim.",
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: true,
      });
    } catch (error) {
      navigate("/home", { replace: true });
      await Swal.fire({
        icon: "error",
        title: "Gagal Mengirim",
        text: "Terjadi kesalahan saat mengirim hasil tes Anda. Silakan coba lagi.",
      });
    }

    //? Console Untuk debugging hasil akhir
    // console.log("Hasil akhir:", counts, results);
  }, [selectedOptions, time, formatTime, psikotesId, user, navigate]);

  useEffect(() => {
    if (handleSubmitRef) {
      handleSubmitRef.current = handleSubmit;
    }
  }, [handleSubmitRef, handleSubmit]);

  return (
    <Container fluid className="psikotes-container">
      <PsikotesHeader timer={<Timer />} />
      <PsikotesContent
        handleSubmit={handleSubmit}
        selectedOptions={selectedOptions}
        handleOptionChange={handleOptionChange}
        questions={questions}
        loading={loading}
        currentPage={currentPage}
        cardsPerPage={cardsPerPage}
        setCurrentPage={setCurrentPage}
        completedPages={completedPages}
        setCompletedPages={setCompletedPages}
      />
    </Container>
  );
}

export default Psikotes;
